import React, { useState } from 'react';
import { Users, Plus, Edit, Trash2, Search, Filter, User, Phone, Mail, MapPin, Building, Calendar, DollarSign, Truck, UserCheck, UserPlus, Grid, List, CheckCircle } from 'lucide-react';
import { formatDZD } from '../utils/currency';
import { useClients } from '../contexts/ClientsContext';
import './Clients.css';

const Clients = () => {
  const { 
    clients, 
    suppliers, 
    addClient, 
    updateClient, 
    deleteClient, 
    addSupplier, 
    updateSupplier, 
    deleteSupplier 
  } = useClients();
  
  const [activeTab, setActiveTab] = useState('clients'); // 'clients' ou 'suppliers'
  const [viewMode, setViewMode] = useState('cards'); // 'table' or 'cards'
  
  // Les données sont maintenant gérées par le contexte ClientsContext

  const [categories] = useState([
    'Technologie', 'Santé', 'Éducation', 'Construction', 'Commerce', 'Innovation', 'Services', 'Industrie', 'Fournitures', 'Équipements'
  ]);

  const wilayas = [
    'Adrar', 'Chlef', 'Laghouat', 'Oum El Bouaghi', 'Batna', 'Béjaïa', 'Biskra', 'Béchar',
    'Blida', 'Bouira', 'Tamanrasset', 'Tébessa', 'Tlemcen', 'Tiaret', 'Tizi Ouzou', 'Alger',
    'Djelfa', 'Jijel', 'Sétif', 'Saïda', 'Skikda', 'Sidi Bel Abbès', 'Annaba', 'Guelma',
    'Constantine', 'Médéa', 'Mostaganem', 'MSila', 'Mascara', 'Ouargla', 'Oran', 'El Bayadh'
  ];

  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formData, setFormData] = useState({
    type: 'company',
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    registrationNumber: '',
    taxNumber: '',
    // Nouvelles informations fiscales obligatoires pour les factures PDF
    fiscalId: '',        // ID Fiscal
    article: '',         // Article
    rc: '',             // RC N°
    contactPhone: '',   // Contact Tel (peut être différent du téléphone principal)
    category: '',
    status: 'active',
    // Champs spécifiques aux fournisseurs
    specialties: [],
    paymentTerms: '',
    deliveryTime: ''
  });

  const clientTypes = {
    company: { label: 'Entreprise', icon: Building },
    individual: { label: 'Particulier', icon: User },
    institution: { label: 'Institution', icon: Building }
  };

  // Données actuelles selon l'onglet actif
  const currentData = activeTab === 'clients' ? clients : suppliers;

  const filteredData = currentData.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || item.type === filterType;
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    
    return matchesSearch && matchesType && matchesCategory && matchesStatus;
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingClient) {
      const updatedData = { 
        ...formData,
        contactPerson: formData.type === 'individual' ? formData.name : formData.contactPerson
      };
      
      if (activeTab === 'clients') {
        updateClient(editingClient.id, updatedData);
      } else {
        updateSupplier(editingClient.id, updatedData);
      }
    } else {
      const newItem = {
        ...formData,
        contactPerson: formData.type === 'individual' ? formData.name : formData.contactPerson,
        createdDate: new Date().toISOString().split('T')[0],
        lastOrder: null,
        totalOrders: 0,
        totalAmount: 0
      };
      
      if (activeTab === 'clients') {
        addClient(newItem);
      } else {
        addSupplier(newItem);
      }
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      type: 'company',
      name: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      registrationNumber: '',
      taxNumber: '',
      // Nouvelles informations fiscales
      fiscalId: '',
      article: '',
      rc: '',
      contactPhone: '',
      category: '',
      status: 'active',
      specialties: [],
      paymentTerms: '',
      deliveryTime: ''
    });
    setEditingClient(null);
    setShowModal(false);
  };

  const handleEdit = (item) => {
    setEditingClient(item);
    
    if (item) {
      // Editing existing client or supplier
      setFormData({
        type: item.type,
        name: item.name,
        contactPerson: item.contactPerson,
        email: item.email,
        phone: item.phone,
        address: item.address,
        city: item.city,
        postalCode: item.postalCode,
        registrationNumber: item.registrationNumber,
        taxNumber: item.taxNumber,
        // Nouvelles informations fiscales
        fiscalId: item.fiscalId || '',
        article: item.article || '',
        rc: item.rc || '',
        contactPhone: item.contactPhone || '',
        category: item.category,
        status: item.status,
        specialties: item.specialties || [],
        paymentTerms: item.paymentTerms || '',
        deliveryTime: item.deliveryTime || ''
      });
    } else {
      // Creating new client or supplier
      setFormData({
        type: 'company',
        name: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postalCode: '',
        registrationNumber: '',
        taxNumber: '',
        fiscalId: '',
        article: '',
        rc: '',
        contactPhone: '',
        category: '',
        status: 'active',
        specialties: [],
        paymentTerms: '',
        deliveryTime: ''
      });
    }
    
    setShowModal(true);
  };

  const handleDelete = (id) => {
    const itemType = activeTab === 'clients' ? 'client' : 'fournisseur';
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ce ${itemType} ?`)) {
      if (activeTab === 'clients') {
        deleteClient(id);
      } else {
        deleteSupplier(id);
      }
    }
  };

  const stats = {
    total: currentData.length,
    active: currentData.filter(c => c.status === 'active').length,
    companies: currentData.filter(c => c.type === 'company').length,
    individuals: currentData.filter(c => c.type === 'individual').length,
    totalRevenue: currentData.reduce((sum, c) => sum + c.totalAmount, 0)
  };

  return (
    <div className="clients fade-in">
      <div className="page-header">
        <h1 className="page-title" style={{ color: '#ffffff' }}>
          <Users size={32} />
          Gestion des Partenaires
        </h1>
        <p className="page-subtitle" style={{ color: '#ffffff' }}>Gérez vos clients et fournisseurs</p>
      </div>

      {/* Tabs Navigation */}
      <div className="tabs-navigation">
        <button 
          className={`tab-button ${activeTab === 'clients' ? 'active' : ''}`}
          onClick={() => setActiveTab('clients')}
        >
          <UserCheck size={20} />
          Clients ({clients.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'suppliers' ? 'active' : ''}`}
          onClick={() => setActiveTab('suppliers')}
        >
          <Truck size={20} />
          Fournisseurs ({suppliers.length})
        </button>
      </div>

      {/* Toolbar */}
      <div className="clients-toolbar">
        <div className="search-filters">
          <div className="search-input">
            <Search size={20} />
            <input
              type="text"
              placeholder="Rechercher un client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous types</option>
            {Object.entries(clientTypes).map(([key, type]) => (
              <option key={key} value={key}>{type.label}</option>
            ))}
          </select>
          <select 
            value={filterCategory} 
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">Toutes catégories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </select>
        </div>
        <div className="toolbar-actions">
          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Vue cartes"
            >
              <Grid size={18} />
            </button>
            <button
              className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vue tableau"
            >
              <List size={18} />
            </button>
          </div>
          <button 
            className="btn-primary add-client-btn"
            onClick={() => handleEdit(null)}
          >
            <Plus size={16} />
            <span>{ activeTab === 'clients' ? 'Nouveau client' : 'Nouveau fournisseur' }</span>
          </button>
        </div>
      </div>

      {/* Stats Cards - COMPLETELY REWRITTEN SECTION */}
      <div className="clients-stats">
        {/* Total Clients Card */}
        <div className="stat-item">
          <div className="stat-icon active">
            <Users size={24} />
          </div>
          <span className="stat-number">{stats.total}</span>
          <span className="stat-label">TOTAL {activeTab === 'clients' ? 'CLIENTS' : 'FOURNISSEURS'}</span>
        </div>
        
        {/* Active Clients Card */}
        <div className="stat-item">
          <div className="stat-icon active">
            <CheckCircle size={24} />
          </div>
          <span className="stat-number">{stats.active}</span>
          <span className="stat-label">ACTIFS</span>
        </div>
        
        {/* Companies Card */}
        <div className="stat-item">
          <div className="stat-icon companies">
            <Building size={24} />
          </div>
          <span className="stat-number">{stats.companies}</span>
          <span className="stat-label">ENTREPRISES</span>
        </div>
        
        {/* Individuals Card */}
        <div className="stat-item">
          <div className="stat-icon individuals">
            <User size={24} />
          </div>
          <span className="stat-number">{stats.individuals}</span>
          <span className="stat-label">PARTICULIERS</span>
        </div>
        
        {/* Revenue Card */}
        <div className="stat-item">
          <div className="stat-icon revenue">
            <DollarSign size={24} />
          </div>
          <span className="stat-number">
            {stats.totalRevenue.toLocaleString('fr-FR')} DA
          </span>
          <span className="stat-label">{activeTab === 'clients' ? 'CA TOTAL' : 'ACHATS TOTAL'}</span>
        </div>
      </div>
      
      {/* Partners Display */}
      {viewMode === 'cards' ? (
      <div className="clients-grid">
        {filteredData.map(item => {
          const TypeIcon = clientTypes[item.type].icon;
          return (
            <div key={item.id} className="client-card">
              <div className="client-header">
                <div className="client-type">
                  <TypeIcon size={20} />
                  <span>{clientTypes[item.type].label}</span>
                </div>
                <div className="client-actions">
                  <button 
                    className="btn-icon"
                    onClick={() => handleEdit(item)}
                    title="Modifier"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    className="btn-icon delete"
                    onClick={() => handleDelete(item.id)}
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="client-content">
                <h3 className="client-name">{item.name}</h3>
                {item.type !== 'individual' && (
                  <div className="client-contact">
                    <User size={14} />
                    {item.contactPerson}
                  </div>
                )}
                <div className="client-email">
                  <Mail size={14} />
                  {item.email}
                </div>
                <div className="client-phone">
                  <Phone size={14} />
                  {item.phone}
                </div>
                <div className="client-address">
                  <MapPin size={14} />
                  {item.address}, {item.city}
                </div>
                {item.registrationNumber && (
                  <div className="client-registration">
                    <Building size={14} />
                    {item.registrationNumber}
                  </div>
                )}
                
                {/* Informations spécifiques aux fournisseurs */}
                {activeTab === 'suppliers' && item.specialties && (
                  <div className="supplier-specialties">
                    <strong>Spécialités:</strong> {item.specialties.join(', ')}
                  </div>
                )}
                {activeTab === 'suppliers' && item.paymentTerms && (
                  <div className="supplier-payment">
                    <strong>Paiement:</strong> {item.paymentTerms}
                  </div>
                )}
                {activeTab === 'suppliers' && item.deliveryTime && (
                  <div className="supplier-delivery">
                    <strong>Livraison:</strong> {item.deliveryTime}
                  </div>
                )}
              </div>

              <div className="client-stats">
                <div className="stat">
                  <span className="stat-value">{item.totalOrders}</span>
                  <span className="stat-label">Commandes</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{formatDZD(item.totalAmount)}</span>
                  <span className="stat-label">{activeTab === 'clients' ? 'CA total' : 'Achats total'}</span>
                </div>
              </div>

              <div className="client-footer">
                <span className="client-category">{item.category}</span>
                <span className={`client-status ${item.status}`}>
                  {item.status === 'active' ? 'Actif' : 'Inactif'}
                </span>
              </div>
            </div>
          );
        })}
        </div>
      ) : (
        <div className="clients-table-container">
          <div className="table-header">
            <div>Partenaire</div>
            <div>Type</div>
            <div>Contact</div>
            <div>Email</div>
            <div>Téléphone</div>
            <div>Ville</div>
            <div>Commandes</div>
            <div>Statut</div>
            <div>Actions</div>
      </div>
          
          {filteredData.map(item => {
            const TypeIcon = clientTypes[item.type].icon;
            return (
              <div key={item.id} className="table-row">
                <div className="table-cell">
                  <div className="client-info">
                    <div className="client-type-icon">
                      {item.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="client-details">
                      <span className="client-name-table">{item.name}</span>
                      <span className="client-category-table">{item.category}</span>
                    </div>
                  </div>
                </div>
                <div className="table-cell">
                  <span className="type-badge">{clientTypes[item.type].label}</span>
                </div>
                <div className="table-cell">
                  {item.type !== 'individual' ? item.contactPerson : '-'}
                </div>
                <div className="table-cell">
                  <div className="contact-info">
                    <Mail size={14} />
                    <span>{item.email}</span>
                  </div>
                </div>
                <div className="table-cell">
                  <div className="contact-info">
                    <Phone size={14} />
                    <span>{item.phone}</span>
                  </div>
                </div>
                <div className="table-cell">
                  <div className="location-info">
                    <MapPin size={14} />
                    <span>{item.city}</span>
                  </div>
                </div>
                <div className="table-cell">
                  <div className="orders-info">
                    <span className="orders-count">{item.totalOrders}</span>
                    <span className="orders-amount">{formatDZD(item.totalAmount)}</span>
                  </div>
                </div>
                <div className="table-cell">
                  <span className={`client-status ${item.status}`}>
                    {item.status === 'active' ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <div className="table-cell actions">
                  <button 
                    className="btn-icon"
                    onClick={() => handleEdit(item)}
                    title="Modifier"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    className="btn-icon delete"
                    onClick={() => handleDelete(item.id)}
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingClient 
                  ? `Modifier le ${activeTab === 'clients' ? 'client' : 'fournisseur'}` 
                  : `Nouveau ${activeTab === 'clients' ? 'client' : 'fournisseur'}`
                }
              </h2>
              <button className="modal-close" onClick={resetForm}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="client-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Type de client *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    required
                  >
                    {Object.entries(clientTypes).map(([key, type]) => (
                      <option key={key} value={key}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{formData.type === 'individual' ? 'Nom complet' : 'Nom de l\'entreprise'} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder={formData.type === 'individual' ? 'Dr. Fatima Benali' : 'SARL TechnoAlger'}
                    required
                  />
                </div>
                {formData.type !== 'individual' && (
                  <div className="form-group">
                    <label>Personne de contact</label>
                    <input
                      type="text"
                      value={formData.contactPerson}
                      onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                      placeholder="Ahmed Belkacem"
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="contact@entreprise.dz"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+213 21 123 456"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Adresse *</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="15 Rue Didouche Mourad"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Ville/Wilaya *</label>
                  <select
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    required
                  >
                    <option value="">Sélectionner une wilaya</option>
                    {wilayas.map(wilaya => (
                      <option key={wilaya} value={wilaya}>{wilaya}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Code postal</label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                    placeholder="16000"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                {formData.type !== 'individual' && (
                  <div className="form-group">
                    <label>N° d'enregistrement</label>
                    <input
                      type="text"
                      value={formData.registrationNumber}
                      onChange={(e) => setFormData({...formData, registrationNumber: e.target.value})}
                      placeholder="RC 16/001234"
                    />
                  </div>
                )}
              </div>

              {formData.type !== 'individual' && (
                <div className="form-group">
                  <label>Numéro fiscal (NIF)</label>
                  <input
                    type="text"
                    value={formData.taxNumber}
                    onChange={(e) => setFormData({...formData, taxNumber: e.target.value})}
                    placeholder="NIF 098765432109"
                  />
                </div>
              )}

              {/* Section Informations Fiscales - Obligatoires pour les factures PDF */}
              <div className="form-section-title">
                <h3>Informations fiscales (pour les factures PDF)</h3>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>ID Fiscal * <span className="required-note">(Obligatoire pour factures)</span></label>
                  <input
                    type="text"
                    value={formData.fiscalId}
                    onChange={(e) => setFormData({...formData, fiscalId: e.target.value})}
                    placeholder="163190104418159"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Article * <span className="required-note">(Obligatoire pour factures)</span></label>
                  <input
                    type="text"
                    value={formData.article}
                    onChange={(e) => setFormData({...formData, article: e.target.value})}
                    placeholder="1901022407"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>RC N° * <span className="required-note">(Obligatoire pour factures)</span></label>
                  <input
                    type="text"
                    value={formData.rc}
                    onChange={(e) => setFormData({...formData, rc: e.target.value})}
                    placeholder="19/00-0433048A99"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Contact Tel * <span className="required-note">(Pour factures)</span></label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({...formData, contactPhone: e.target.value})}
                    placeholder="036 62 60 08"
                    required
                  />
                </div>
              </div>

              {/* Champs spécifiques aux fournisseurs */}
              {activeTab === 'suppliers' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Spécialités</label>
                      <input
                        type="text"
                        value={formData.specialties.join(', ')}
                        onChange={(e) => setFormData({
                          ...formData, 
                          specialties: e.target.value.split(', ').filter(s => s.trim())
                        })}
                        placeholder="Matériel informatique, Logiciels"
                      />
                      <small>Séparez les spécialités par des virgules</small>
                    </div>
                    <div className="form-group">
                      <label>Conditions de paiement</label>
                      <select
                        value={formData.paymentTerms}
                        onChange={(e) => setFormData({...formData, paymentTerms: e.target.value})}
                      >
                        <option value="">Sélectionner</option>
                        <option value="15 jours">15 jours</option>
                        <option value="30 jours">30 jours</option>
                        <option value="45 jours">45 jours</option>
                        <option value="60 jours">60 jours</option>
                        <option value="Comptant">Comptant</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Délai de livraison</label>
                    <select
                      value={formData.deliveryTime}
                      onChange={(e) => setFormData({...formData, deliveryTime: e.target.value})}
                    >
                      <option value="">Sélectionner</option>
                      <option value="1-2 jours">1-2 jours</option>
                      <option value="2-3 jours">2-3 jours</option>
                      <option value="3-5 jours">3-5 jours</option>
                      <option value="5-7 jours">5-7 jours</option>
                      <option value="7-10 jours">7-10 jours</option>
                      <option value="10-15 jours">10-15 jours</option>
                    </select>
                  </div>
                </>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingClient ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients; 